import React from "react";

export const HistoryRow = ({
  subject,
  icon,
  date,
  type,
  score,
  status,
  statusColor,
  onDelete,
  onReview,
  onStatusClick,
  onTitleClick,
  isSelected = false,
  onToggleSelect,
}) => {
  const iconStyles = { 
    orange: "bg-amber-100 text-amber-800 border-amber-200", 
    red: "bg-rose-100 text-rose-800 border-rose-200", 
    blue: "bg-blue-100 text-blue-800 border-blue-200", 
    green: "bg-emerald-100 text-emerald-800 border-emerald-200" 
  };
  const statusStyles = { 
    green: "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100/80 hover:border-emerald-400", 
    orange: "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100/80 hover:border-amber-400", 
    red: "bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100/80 hover:border-rose-400" 
  };
  const scoreColors = { 
    green: "text-emerald-700 font-extrabold", 
    orange: "text-amber-700 font-extrabold", 
    red: "text-rose-700 font-extrabold" 
  };
  const statusIcons = {
    green: "fa-circle-check text-emerald-600",
    orange: "fa-circle-dot text-amber-600",
    red: "fa-arrows-rotate text-rose-600"
  };

  const handleStatusAction = onStatusClick || onReview;
  const handleTitleAction = onTitleClick || onReview;

  return (
    <tr
      className={`border-b border-slate-100 transition group ${
        isSelected ? "bg-indigo-50/60" : "hover:bg-slate-50/80"
      }`}
    >
      {onToggleSelect && (
        <td className="py-4 pl-4 w-10 text-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer transition"
            aria-label={`Select ${subject}`}
          />
        </td>
      )}
      <td className={`py-4 ${onToggleSelect ? "px-3" : "pl-4"} font-bold text-slate-900 flex items-center gap-3`}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs border shrink-0 ${iconStyles[statusColor]}`}>
          <i className={`fa-solid ${icon}`} aria-hidden="true"></i>
        </div>
        {handleTitleAction ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleTitleAction();
            }}
            className="truncate max-w-[220px] text-left hover:text-indigo-600 hover:underline transition-colors cursor-pointer"
            title={`Open module: ${subject}`}
          >
            {subject}
          </button>
        ) : (
          <span className="truncate max-w-[220px]" title={subject}>{subject}</span>
        )}
      </td>
      <td className="py-4 px-3 text-slate-600 font-medium whitespace-nowrap">{date}</td>
      <td className="py-4 px-3 text-slate-700 font-semibold">{type}</td>
      <td className={`py-4 px-3 font-mono text-base ${scoreColors[statusColor]}`}>{score}</td>
      <td className="py-4 px-3">
        {handleStatusAction ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleStatusAction();
            }}
            className={`${statusStyles[statusColor]} group/badge inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-xs cursor-pointer hover:shadow-md hover:scale-[1.03] active:scale-95 transition-all`}
            title={`Click to review ${subject}`}
            aria-label={`Review ${subject} module (${status})`}
          >
            <i className={`fa-solid ${statusIcons[statusColor]}`} aria-hidden="true"></i>
            <span>{status}</span>
            <i className="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-60 group-hover/badge:opacity-100 group-hover/badge:translate-x-0.5 transition-all" aria-hidden="true"></i>
          </button>
        ) : (
          <span className={`${statusStyles[statusColor]} inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-xs`}>
            <i className={`fa-solid ${statusIcons[statusColor]}`} aria-hidden="true"></i>
            {status}
          </span>
        )}
      </td>
      <td className="py-4 pr-4 text-center">
        <div className="flex items-center justify-center gap-1">
          {onReview && (
            <button
              type="button"
              onClick={onReview}
              className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title="Review module"
              aria-label={`Review ${subject} module`}
            >
              <i className="fa-solid fa-book-open text-sm" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
              title="Delete attempt"
              aria-label="Delete quiz attempt"
            >
              <i className="fa-solid fa-trash-can text-sm" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export const ModalHistoryRow = ({
  rank,
  subject,
  date,
  type,
  score,
  status,
  statusColor,
  onDelete,
  onReview,
  onStatusClick,
  onTitleClick,
  isSelected = false,
  onToggleSelect,
}) => {
  const statusStyles = { 
    green: "bg-green-50 text-green-700 border-green-200 hover:bg-green-100 hover:border-green-300", 
    orange: "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 hover:border-orange-300", 
    red: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-300" 
  };
  const scoreColors  = { 
    green: "text-green-600 font-bold", 
    orange: "text-orange-500 font-bold", 
    red: "text-red-500 font-bold" 
  };
  const iconStyles   = { 
    green: "bg-green-50 text-green-600", 
    orange: "bg-orange-50 text-orange-600", 
    red: "bg-red-50 text-red-600" 
  };

  const handleStatusAction = onStatusClick || onReview;
  const handleTitleAction = onTitleClick || onReview;

  return (
    <tr
      className={`border-b border-gray-50 transition ${
        isSelected ? "bg-indigo-50/60" : "hover:bg-gray-50"
      }`}
    >
      {onToggleSelect && (
        <td className="py-3 pl-3 w-10 text-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300 cursor-pointer transition"
            aria-label={`Select ${subject}`}
          />
        </td>
      )}
      <td className="py-3 pl-2 text-xs text-gray-400 font-mono w-8">{rank}</td>
      <td className="py-3 font-bold text-gray-800">
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 rounded flex items-center justify-center text-xs flex-shrink-0 ${iconStyles[statusColor]}`}>
            <i className="fa-solid fa-file-lines" />
          </div>
          {handleTitleAction ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleTitleAction();
              }}
              className="truncate max-w-[220px] text-left hover:text-indigo-600 hover:underline transition-colors cursor-pointer"
              title={`Open module: ${subject}`}
            >
              {subject}
            </button>
          ) : (
            <span className="truncate max-w-[220px]" title={subject}>{subject}</span>
          )}
        </div>
      </td>
      <td className="py-3 text-gray-500 whitespace-nowrap text-xs">{date}</td>
      <td className="py-3 text-gray-500 text-xs">{type}</td>
      <td className={`py-3 ${scoreColors[statusColor]}`}>{score}</td>
      <td className="py-3">
        {handleStatusAction ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleStatusAction();
            }}
            className={`${statusStyles[statusColor]} group/modalBadge inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border cursor-pointer hover:shadow-sm hover:scale-[1.03] active:scale-95 transition-all`}
            title={`Click to review ${subject}`}
            aria-label={`Review ${subject} module (${status})`}
          >
            <span>{status}</span>
            <i className="fa-solid fa-arrow-up-right-from-square text-[9px] opacity-60 group-hover/modalBadge:opacity-100 transition-opacity" aria-hidden="true"></i>
          </button>
        ) : (
          <span className={`${statusStyles[statusColor]} px-2.5 py-1 rounded-full text-xs font-bold border`}>{status}</span>
        )}
      </td>
      <td className="py-3 pr-2 text-center">
        <div className="flex items-center justify-center gap-1">
          {onReview && (
            <button
              type="button"
              onClick={onReview}
              className="w-7 h-7 rounded-lg inline-flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title="Review module"
              aria-label={`Review ${subject} module`}
            >
              <i className="fa-solid fa-book-open text-xs" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="w-7 h-7 rounded-lg inline-flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Delete attempt"
              aria-label="Delete quiz attempt"
            >
              <i className="fa-solid fa-trash-can text-xs" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
